"use client";

import "../opportunities.css";

import { PUBLIC_LISTINGS_MAX_LIMIT } from "@repo/api/use-cases/opportunity-listing/limits";
import { COMPANY_EMAILS } from "@repo/common";
import { Skeleton } from "@repo/ui";
import { ArrowRight, Check, Mail, Plus } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import { MarketingVideo } from "@/components/marketing/marketing-video";
import {
  AdvertisedRatesProvider,
  useAdvertisedRates,
} from "@/components/opportunities/advertised-pay";
import { OpportunityCard } from "@/components/opportunities/opportunity-card";
import { formatCount, NETWORK_STATS } from "@/config/network-stats";
import {
  TRUST_VIDEO_POSTER,
  TRUST_VIDEO_SRC,
  TRUST_VIDEO_TITLE,
} from "@/config/trust-video";
import { useParticipationStatuses } from "@/hooks/use-participation-statuses";
import { useSession } from "@/hooks/use-session";
import { shouldHideFromBrowse } from "@/lib/opportunity-utils";
import { api } from "@/lib/trpc/react";

const WAVE_BARS = [14, 22, 10, 28, 18, 24, 12, 16, 26, 14, 20, 8].map(
  (h, i) => ({ id: `w${i}`, h, delay: i * -0.08 }),
);

const FAQ_ITEMS: { q: string; a: React.ReactNode }[] = [
  {
    q: "How much can I actually earn on Terac?",
    a: "It varies by opportunity. Every listing shows its own pay range and structure upfront, so you know what you'll earn before you apply.",
  },
  {
    q: "How long does it take to get started?",
    a: "As fast as you want. Submit the application and you can start immediately, apply in a couple of clicks, complete a short voice screening with our AI interviewer, and you're in.",
  },
  {
    q: "When and how do I get paid?",
    a: "As soon as your submission is approved, the payment lands in your Terac wallet and you're able to withdraw any time, to your bank, PayPal, or as a gift card.",
  },
  {
    q: "Do I need any special equipment?",
    a: "Just a laptop or phone with a working microphone and stable internet. Video sessions require a webcam. Everything runs in your browser, no downloads required.",
  },
  {
    q: "Who sees my responses and data?",
    a: (
      <>
        Your responses are shared only with the research team running the study
        you&apos;re matched to. We never sell your data. Reach us at{" "}
        <Link
          href={`mailto:${COMPANY_EMAILS.support}`}
          className="text-marketing-text decoration-marketing-border hover:decoration-marketing-text underline underline-offset-[3px]"
        >
          {COMPANY_EMAILS.support}
        </Link>{" "}
        with any privacy questions.
      </>
    ),
  },
  {
    q: "Can I refer other experts?",
    a: "Yes, and you'll earn up to $1,000 per successful referral. Every opportunity card has a Refer button. Share your link, and you get paid when your referral completes their first session.",
  },
];

interface FeaturedExpert {
  name: string;
  image: string;
  completedInterviews: number;
}

const FEATURED_EXPERTS: FeaturedExpert[] = [
  {
    name: "Anna Kovalenko",
    image: "/marketing/experts/expert-unsplash-1.jpg",
    completedInterviews: 412,
  },
  {
    name: "Marcus Lindgren",
    image: "/marketing/experts/expert-unsplash-2.jpg",
    completedInterviews: 287,
  },
  {
    name: "Priya Raghavan",
    image: "/marketing/experts/expert-unsplash-3.jpg",
    completedInterviews: 196,
  },
  {
    name: "Daniel Schaefer",
    image: "/marketing/experts/expert-unsplash-4.jpg",
    completedInterviews: 152,
  },
  {
    name: "Hiro Tanaka",
    image: "/marketing/experts/expert-1.png",
    completedInterviews: 119,
  },
];

export function OpportunitiesPageContent() {
  const { user } = useSession();

  // Same constant the router validates against, and the same input page.tsx
  // prefetches with: the hydration key is built from it, so a mismatch would
  // silently refetch everything on the client.
  const { data, isLoading } =
    api.opportunityListing.listPublicListings.useQuery({
      limit: PUBLIC_LISTINGS_MAX_LIMIT,
    });

  const publicListings = data?.items ?? [];

  const { statusMap } = useParticipationStatuses({
    enabled: Boolean(user),
  });

  const opportunities = publicListings.filter((opp) => {
    if (!opp.studyId) {
      return true;
    }
    return !shouldHideFromBrowse(statusMap.get(opp.studyId) ?? undefined);
  });

  // Keyed off the whole published set rather than the cards that survive the
  // participation filter: the answer is the same for either, narrowing it would
  // refetch whenever a status lands, and it is the set page.tsx prefetches the
  // answer for, so the first paint already carries every figure.
  const rates = useAdvertisedRates(
    publicListings.map((listing) => listing.studyId),
  );

  const openCount = opportunities.length;
  const heroVerified = FEATURED_EXPERTS.slice(0, 4);
  const networkAvatars = FEATURED_EXPERTS.slice(0, 5);

  const scrollToGrid = () => {
    document
      .getElementById("opportunities-grid")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-marketing-cream text-marketing-text">
      {/* HERO */}
      <section className="px-5 pt-16 pb-12 text-center sm:pt-20 sm:pb-14">
        <div className="mx-auto max-w-[1200px]">
          <div className="border-marketing-border bg-marketing-cream-light text-marketing-text text-copy-13 inline-flex items-center gap-2 rounded-full border px-3 py-0.5 font-medium">
            <span className="bg-marketing-accent-green-dark h-1.5 w-1.5 rounded-full" />
            {isLoading ? (
              <Skeleton className="h-3 w-32" />
            ) : (
              `${openCount} ${openCount === 1 ? "opportunity" : "opportunities"} open today`
            )}
          </div>
          <h1 className="text-marketing-text text-heading-32 sm:text-heading-48 lg:text-heading-56 mx-auto mt-7 max-w-[820px] text-balance">
            Join the team building{" "}
            <span className="text-marketing-text-secondary">
              the future of research.
            </span>
          </h1>
          <p className="text-marketing-text-muted text-copy-16 sm:text-copy-18 mx-auto mt-6 max-w-[560px]">
            Discover paid opportunities to share your expertise. Flexible hours,
            competitive pay, and meaningful work in AI research.
          </p>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section id="opps" className="px-5 pt-8 pb-20 sm:pt-12 sm:pb-24">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="text-marketing-text text-heading-24! sm:text-heading-32! mb-6 text-balance">
            Explore opportunities
          </h2>

          <div
            id="opportunities-grid"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {isLoading ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`skeleton-${idx}`}
                  className="border-marketing-border-light bg-marketing-cream-light flex min-h-[168px] flex-col rounded-xl border px-4 py-4"
                >
                  <div className="mb-3 flex items-center gap-1.5">
                    <Skeleton className="h-[15px] w-[15px] rounded-sm" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                  <Skeleton className="mb-1.5 h-[18px] w-3/4" />
                  <Skeleton className="mb-1 h-3 w-full" />
                  <Skeleton className="mb-3.5 h-3 w-2/3" />
                  <div className="mt-auto flex items-center gap-2.5">
                    <Skeleton className="h-4 w-24" />
                    <div className="ml-auto flex items-center gap-1.5">
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                  </div>
                </div>
              ))
            ) : opportunities.length === 0 ? (
              <div className="col-span-full py-16 text-center">
                <p className="text-marketing-text-muted text-copy-16">
                  No opportunities available right now. Check back soon.
                </p>
              </div>
            ) : (
              <AdvertisedRatesProvider rates={rates}>
                {opportunities.map((opp) => (
                  <OpportunityCard
                    key={opp.id}
                    opportunity={opp}
                    submissionStatus={
                      opp.studyId ? (statusMap.get(opp.studyId) ?? null) : null
                    }
                  />
                ))}
              </AdvertisedRatesProvider>
            )}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="border-marketing-border-light border-t px-5 py-20 sm:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-10 grid items-end gap-8 sm:mb-12 sm:grid-cols-[1fr_1.2fr] sm:gap-16">
            <div>
              <div className="text-marketing-text-muted mb-3 text-[12px] font-semibold tracking-[0.14em] uppercase">
                Walkthrough
              </div>
              <h2 className="text-marketing-text text-heading-24! sm:text-heading-32! text-balance">
                See how Terac works.
              </h2>
            </div>
            <p className="text-marketing-text-muted text-copy-16 sm:text-copy-18 max-w-[480px]">
              Two minutes on the expert network paying real people for short,
              remote conversations with the teams building AI.
            </p>
          </div>

          <div className="border-marketing-border-light bg-marketing-text relative overflow-hidden rounded-[18px] border">
            <div
              className="pointer-events-none absolute inset-0 opacity-100"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "56px 56px",
              }}
            />
            <div className="relative aspect-[16/8] w-full">
              <MarketingVideo
                src={TRUST_VIDEO_SRC}
                poster={TRUST_VIDEO_POSTER}
                title={TRUST_VIDEO_TITLE}
                preload="none"
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-muted border-marketing-border-light border-y px-5 py-20 sm:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-12 grid items-end gap-8 sm:grid-cols-[1fr_1.2fr] sm:gap-16">
            <div>
              <div className="text-marketing-text-muted mb-3 text-[12px] font-semibold tracking-[0.14em] uppercase">
                How it works
              </div>
              <h2 className="text-marketing-text text-heading-24! sm:text-heading-32! text-balance">
                From application to earning.
              </h2>
            </div>
            <p className="text-marketing-text-muted text-copy-16 sm:text-copy-18 max-w-[480px]">
              Getting started takes minutes. Here&apos;s the full path from your
              first application to your first payout, and beyond.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <StepCard
              num="1"
              label="Choose & Apply"
              title="Pick an opportunity that fits you"
              desc="Browse open roles that match your skills. Apply in minutes, no cover letter, no hassle. You're in control of what you take on."
            >
              <ApplyListMock />
            </StepCard>

            <StepCard
              num="2"
              label="Quick Screening"
              title="A short conversation with our AI"
              desc="Have a short voice conversation with our AI interviewer. It assesses your skills and matches you to the right roles, no scheduling required."
            >
              <VoiceMock />
            </StepCard>

            <StepCard
              num="3"
              label="Get Matched & Earn"
              title="Start working and getting paid instantly"
              desc="We match you with opportunities that fit your expertise. Start immediately and get paid hourly. Fast, reliable payouts to your wallet."
            >
              <EarnMock />
            </StepCard>

            <StepCard
              num="4"
              label="Join the Network"
              title="Become part of Terac's global talent pool"
              desc="Once you're in, you're in. Get priority access to new opportunities matched to your expertise, and grow with us as the network expands."
            >
              <NetworkMock avatars={networkAvatars} />
            </StepCard>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-marketing-border-light border-t px-5 py-20 sm:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="grid items-start gap-12 sm:grid-cols-[1fr_1.4fr] sm:gap-20">
            <div>
              <div className="text-marketing-text-muted mb-4 text-[12px] font-semibold tracking-[0.14em] uppercase">
                FAQ
              </div>
              <h2 className="text-marketing-text text-heading-24! sm:text-heading-32! text-balance">
                Questions, answered.
              </h2>
              <p className="text-marketing-text-muted text-copy-16 mt-6 max-w-[380px]">
                Everything you need to know about working with Terac. Don&apos;t
                see your question here? Reach out anytime.
              </p>
              <Link
                href={`mailto:${COMPANY_EMAILS.support}`}
                className="text-marketing-text hover:text-marketing-text-muted mt-7 inline-flex items-center gap-2.5 text-[18px] font-semibold transition-colors"
              >
                <Mail className="h-5 w-5" strokeWidth={1.8} />
                {COMPANY_EMAILS.support}
              </Link>
            </div>
            <FaqList />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 py-24 text-center sm:py-28">
        <div className="mx-auto max-w-[1200px]">
          <h2 className="text-heading-32 sm:text-heading-40 mx-auto max-w-[720px] text-balance">
            Ready to shape the
            <br />
            future of AI research?
          </h2>
          <p className="text-marketing-text-muted text-copy-16 sm:text-copy-18 mx-auto mt-5 max-w-[520px]">
            Join hundreds of professionals already working with Terac. Flexible
            hours, competitive pay, and meaningful work, all in one place.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={scrollToGrid}
              className="bg-marketing-text text-marketing-cream hover:bg-marketing-text/90 text-copy-14 inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-colors"
            >
              Browse opportunities
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
            </button>
            <Link
              href="/"
              className="border-marketing-border bg-marketing-cream-light hover:bg-marketing-cream-hover text-marketing-text text-copy-14 inline-flex items-center gap-2 rounded-full border px-6 py-3.5 font-medium transition-colors"
            >
              Learn more about Terac
            </Link>
          </div>
        </div>
      </section>

      {/* RECRUITER BANNER */}
      <section className="px-5 pb-24 sm:pb-28">
        <div className="bg-marketing-blue mx-auto grid max-w-[1200px] items-center gap-12 overflow-hidden rounded-[22px] px-8 py-14 text-white sm:grid-cols-[1.3fr_1fr] sm:px-16 sm:py-20">
          <div>
            <h2 className="text-heading-32 sm:text-heading-48 text-balance text-white">
              Ready to recruit
              <br />
              quality experts, <span className="text-white/40">fast?</span>
            </h2>
            <p className="text-copy-16 mt-5 max-w-[460px] text-white/65">
              Unlock the power of AI-led screening and gain deeper understanding
              of who&apos;s powering your research. Verified, vetted, ready to
              go.
            </p>
            <Link
              href="/contact"
              className="text-marketing-text text-copy-14 mt-7 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-semibold transition-opacity hover:opacity-90"
            >
              Talk to sales
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
            </Link>
          </div>
          <div className="flex flex-col">
            {heroVerified.map((expert, i) => (
              <div
                key={expert.name}
                className={`flex items-center justify-between gap-3 py-4 text-[14px] ${
                  i === heroVerified.length - 1
                    ? ""
                    : "border-b border-white/10"
                } ${i === 0 ? "pt-0" : ""}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Image
                    src={expert.image}
                    alt={expert.name}
                    width={28}
                    height={28}
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                  <span className="truncate text-white/90">
                    <span className="font-medium">{expert.name}</span>
                    <span className="text-white/55">
                      {" "}
                      · {expert.completedInterviews} interviews completed
                    </span>
                  </span>
                </div>
                <span className="text-copy-14 inline-flex shrink-0 items-center gap-2 font-medium text-[#7AE1A6]">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#7AE1A6]/20">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  Verified
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function StepCard({
  num,
  label,
  title,
  desc,
  children,
}: {
  num: string;
  label: string;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <article className="border-marketing-border bg-marketing-cream flex flex-col gap-7 rounded-[18px] border p-7">
      <div>
        <div className="text-marketing-text-muted flex items-center gap-3 text-[11.5px] font-semibold tracking-[0.12em] uppercase">
          <span className="border-marketing-border text-marketing-text inline-flex h-[24px] w-[24px] items-center justify-center rounded-full border text-[11.5px] font-medium tracking-normal normal-case tabular-nums">
            {num}
          </span>
          {label}
        </div>
        <h3 className="text-marketing-text text-heading-24 mt-4 max-w-[380px] font-medium text-balance">
          {title}
        </h3>
        <p className="text-marketing-text-muted text-copy-14 mt-3 max-w-[440px]">
          {desc}
        </p>
      </div>
      <div className="mt-auto">{children}</div>
    </article>
  );
}

function ApplyListMock() {
  const rows: { name: string; dot: string; applied?: boolean }[] = [
    { name: "Financial Professionals", dot: "#D9D5C9", applied: true },
    { name: "Strategic Business Leaders", dot: "#C9CFE0" },
    { name: "Industrial Finance Insights", dot: "#D6E0C9" },
    { name: "Business Process Interview", dot: "#E0CFC9" },
  ];
  return (
    <div className="flex flex-col">
      {rows.map((row, i) => (
        <div
          key={row.name}
          className={`text-copy-14 flex items-center justify-between py-3 ${
            i === rows.length - 1
              ? ""
              : "border-marketing-border-light border-b"
          }`}
        >
          <div className="text-marketing-text flex items-center gap-3 font-medium">
            <span
              className="border-marketing-border h-5 w-5 rounded-full border"
              style={{ background: row.dot }}
            />
            {row.name}
          </div>
          {row.applied ? (
            <span className="text-marketing-accent-green-text inline-flex items-center gap-1 py-1 text-[11.5px] font-semibold">
              <Check className="h-3 w-3" strokeWidth={3} />
              Applied
            </span>
          ) : (
            <span className="bg-marketing-text text-marketing-cream rounded-full px-3 py-1 text-[11.5px] font-semibold">
              Apply
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function VoiceMock() {
  return (
    <div className="flex flex-col gap-5">
      <p className="text-marketing-text text-copy-16 font-medium italic">
        &ldquo;Tell me about a time you had to debug a complex production issue.
        How did you approach it?&rdquo;
      </p>
      <div className="flex items-center gap-3.5">
        <span className="bg-marketing-blue inline-block h-2.5 w-2.5 animate-[liveDot_1.4s_ease-in-out_infinite] rounded-full" />
        <div className="flex flex-1 items-center gap-[3px]">
          {WAVE_BARS.map((bar) => (
            <span
              key={bar.id}
              className="bg-marketing-text inline-block w-[3px] rounded-sm"
              style={{
                height: `${bar.h}px`,
                animation: `waveAnim 1.2s ease-in-out ${bar.delay}s infinite`,
              }}
            />
          ))}
        </div>
        <span className="text-marketing-text-muted text-[12px] font-medium">
          Listening...
        </span>
      </div>
    </div>
  );
}

function EarnMock() {
  return (
    <div>
      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-marketing-text-muted text-[11.5px] font-semibold tracking-[0.1em] uppercase">
            Earned this month
          </div>
          <div className="text-marketing-text mt-1.5 text-[42px] leading-none font-bold tracking-[-0.03em] tabular-nums">
            $5,000
          </div>
        </div>
        <span className="bg-marketing-badge-green-bg text-marketing-accent-green-text rounded-full px-2.5 py-1 text-[12px] font-semibold">
          + $1,200
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {["Instant payouts", "Hourly billing", "Track earnings"].map((t) => (
          <span
            key={t}
            className="border-marketing-border text-marketing-text inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium"
          >
            <span className="bg-marketing-accent-green-dark h-1.5 w-1.5 rounded-full" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function NetworkMock({ avatars }: { avatars: FeaturedExpert[] }) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {avatars.map((u, idx) => (
            <Image
              key={u.name}
              src={u.image}
              alt={u.name}
              width={32}
              height={32}
              className={`border-marketing-cream h-8 w-8 rounded-full border-2 object-cover ${idx === 0 ? "" : "-ml-2.5"}`}
            />
          ))}
          <span className="bg-marketing-text text-marketing-cream border-marketing-cream -ml-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full border-2 text-[10.5px] font-semibold">
            +100k
          </span>
        </div>
        <span className="bg-marketing-badge-green-bg text-marketing-accent-green-text inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold">
          <span className="bg-marketing-accent-green-dark inline-flex h-3 w-3 items-center justify-center rounded-full text-white">
            <Check className="h-1.5 w-1.5" strokeWidth={3.5} />
          </span>
          Matched
        </span>
      </div>
      <div>
        <span className="text-marketing-text text-[42px] leading-none font-bold tracking-[-0.03em] tabular-nums">
          {formatCount(NETWORK_STATS.registered)}
        </span>
        <span className="text-marketing-text-muted ml-2.5 text-[14px] font-medium">
          registered experts and counting
        </span>
      </div>
    </div>
  );
}

function FaqList() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <div className="flex flex-col">
      {FAQ_ITEMS.map((item, idx) => {
        const isOpen = openIdx === idx;
        return (
          <div
            key={item.q}
            className={`border-marketing-border-light border-b ${idx === 0 ? "border-t" : ""}`}
          >
            <button
              type="button"
              onClick={() => {
                setOpenIdx(isOpen ? null : idx);
              }}
              className="flex w-full items-center justify-between gap-6 py-5 text-left transition-colors hover:opacity-80"
            >
              <span className="text-marketing-text sm:text-copy-18 text-[16px] leading-[1.35] font-semibold tracking-[-0.015em]">
                {item.q}
              </span>
              <span
                className={`text-marketing-text-muted flex h-7 w-7 shrink-0 items-center justify-center transition-transform duration-200 ${isOpen ? "text-marketing-text rotate-45" : ""}`}
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
              </span>
            </button>
            <div
              className={`grid transition-[grid-template-rows,padding-bottom,opacity] duration-300 ${
                isOpen
                  ? "grid-rows-[1fr] pb-6 opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <p className="text-marketing-text-muted text-copy-14 sm:text-copy-16 max-w-[620px]">
                  {item.a}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
